import { Actions, createEffect, ofType } from '@ngrx/effects';
import { inject, Injectable } from '@angular/core';
import { saveTokenDetails, ssoActions } from '../../actions/auth.actions';
import { catchError, filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { of } from 'rxjs';
import { AuthBackend } from '../../../backend/auth.backend';
import { AuthConfig, AuthProvider, LoginResponse } from '../../../models';
import { AuthConfigService, AuthLoggerService } from '../../../services';
import { RouterFacade } from '@rx-applications/shared/data-access';
import { AuthFacade } from '../../facade/auth.facade';

@Injectable()
export class SsoEffects {
  actions$ = inject(Actions);
  authBackend = inject(AuthBackend);
  authLoggerService = inject(AuthLoggerService);
  routerFacade = inject(RouterFacade);
  authFacade = inject(AuthFacade);
  authConfigService = inject(AuthConfigService);

  loadSsoLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ssoActions.loadSsoLogin),
      filter(() => !!this.authConfigService?.config.sso.ssoEnabled),
      switchMap(({ websiteId, websiteUrl }) =>
        this.authBackend.getSsoLogin(websiteId, websiteUrl).pipe(
          map((ssoLogin) => ssoActions.loadSsoLoginSucceed({ ssoLogin })),
          catchError((error) => of(ssoActions.loadSsoLoginFailed({ error }))),
        ),
      ),
    ),
  );

  loadSsoLoginSucceed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ssoActions.loadSsoLoginSucceed),
        withLatestFrom(this.routerFacade.queryParams$),
        filter(
          ([{ ssoLogin }, queryParams]) =>
            !!(queryParams && queryParams['idpAuthenticated'] && ssoLogin.identityProviders?.length),
        ),
        tap(() => this.authFacade.goToIdentityProvider()),
      ),
    { dispatch: false },
  );

  goToIdentityProvider$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ssoActions.goToIdentityProvider),
        withLatestFrom(this.authFacade.ssoLoginUrl$),
        tap(([, url]) => (window.location.href = url)),
      ),
    { dispatch: false },
  );

  loginWithSso$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ssoActions.loginWithSso),
      switchMap(({ authCode, websiteId }) =>
        this.authBackend.loginWithSso(authCode, websiteId).pipe(
          map((response: LoginResponse) => {
            this.authLoggerService.loginSuccess({ Username: response?.email, sso: true });
            return ssoActions.loginWithSsoSucceed({ response });
          }),
          catchError((error) => {
            this.authLoggerService.loginFailed({ sso: true });
            return of(ssoActions.loginWithSsoFailed({ error }));
          }),
        ),
      ),
    ),
  );

  loginWithSsoSucceed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ssoActions.loginWithSsoSucceed),
      map(({ response }) => saveTokenDetails({ response })),
    ),
  );
}
